'use client';

import { useMemo } from 'react';
import { ResponsiveContainer, AreaChart as RAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { FinancialIndependenceValues, FinancialIndependenceResult, FinancialIndependenceTranslations } from './types';

type ChartPoint = { 
  year: string; 
  Amount: number; 
  'Total Savings': number; 
  'Target Amount': number;
};

interface Props {
  values: FinancialIndependenceValues;
  result: FinancialIndependenceResult;
  translations: FinancialIndependenceTranslations;
}

export default function FinancialIndependenceChart({ values, result, translations }: Props) {
  const data: ChartPoint[] = useMemo(() => {
    const points: ChartPoint[] = [];
    const { currentSavings, monthlyContribution, investmentReturn } = values;
    const { yearsToIndependence, targetAmount } = result;
    
    const annualReturn = investmentReturn / 100;
    
    for (let year = 0; year <= Math.min(yearsToIndependence, 30); year++) {
      let amount = currentSavings;
      let totalSavings = currentSavings;
      
      for (let y = 1; y <= year; y++) {
        amount = amount * (1 + annualReturn) + (monthlyContribution * 12);
        totalSavings += monthlyContribution * 12;
      }
      
      points.push({
        year: `${year}`,
        Amount: Math.round(amount),
        'Total Savings': Math.round(totalSavings),
        'Target Amount': Math.round(targetAmount)
      });
    }
    
    return points;
  }, [values, result]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = (props: any) => {
    const { payload, active } = props;
    if (!active || !payload || !payload.length) return null;
    const pt = payload[0]?.payload as ChartPoint | undefined;
    if (!pt) return null;
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[280px]">
        <div className="mb-2 pb-2 border-b border-gray-100">
          <div className="text-sm font-semibold text-gray-900">{translations.xAxisLabel}: {pt.year}</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{translations.chartTooltipPortfolioValue}</span>
            <span className="font-semibold text-blue-700">{pt.Amount.toLocaleString('sv-SE')} {values.currency}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{translations.chartTooltipTotalSavings}</span>
            <span className="font-semibold text-emerald-700">{pt['Total Savings'].toLocaleString('sv-SE')} {values.currency}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{translations.chartTooltipTargetAmount}</span>
            <span className="font-semibold text-orange-700">{pt['Target Amount'].toLocaleString('sv-SE')} {values.currency}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow border border-white/20">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">{translations.chartTitle}</h3>
        <p className="text-xs text-gray-600">{translations.chartSubtitle}</p>
      </div>
      
      <div className="relative w-full bg-gray-50/40 rounded-xl pt-5 pb-10 mb-4">
        <span className="absolute left-0 -top-2 text-xs text-gray-500">{`${translations.yAxisLabel.split('(')[0].trim()} (${values.currency})`}</span>
        <span className="absolute right-0 -bottom-2 text-xs text-gray-500">{translations.xAxisLabel}</span>
        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RAreaChart data={data} margin={{ top: 8, right: 8, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" interval="preserveStartEnd" tickMargin={8} />
              <YAxis hide />
              <Tooltip content={CustomTooltip} />
              <Area type="monotone" dataKey="Total Savings" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
              <Area type="monotone" dataKey="Amount" stroke="#3b82f6" fill="#3b82f622" strokeWidth={2} />
              <Area type="monotone" dataKey="Target Amount" stroke="#f59e0b" fill="#f59e0b22" strokeWidth={2} />
            </RAreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Legend explanation */}
      <div className="text-xs text-gray-500 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded"></div>
          <span className="text-xs">{translations.chartLegendTotalSavings}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded"></div>
          <span className="text-xs">{translations.chartLegendPortfolioValue}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-orange-500 rounded"></div>
          <span className="text-xs">{translations.chartLegendTargetAmount}</span>
        </div>
      </div>
    </div>
  );
}
